import { getClients } from "@api/getClients.js";
import createOrderMutation from "@api/mutations/createOrder.graphql";
import getPaymentStatusQuery from "@api/queries/getPaymentStatus.graphql";
import { Auth } from "@utils/Auth.svelte.js";
import { untrack } from "svelte";
import type { Country } from "@core/schema/Country.js";
import type { CountrySettings } from "@core/schema/CountrySettings.js";
import type { Currency } from "@core/schema/Currency.js";
import { DeliveryType } from "@core/schema/DeliveryType.js";
import { EconomicSubjectVatType } from "@core/schema/EconomicSubjectVatType.js";
import type { ExchangeRate } from "@core/schema/ExchangeRatesSetting.js";
import { GoPayPaymentState } from "@core/schema/GoPayPaymentState.js";
import type { Locale } from "@core/schema/Locale.js";
import type { Order, OrderInput, PublicOrder } from "@core/schema/Order.js";
import type { OrderPricesInput } from "@core/schema/OrderPrices.js";
import type { OrderProduct } from "@core/schema/OrderProduct.js";
import type { OrderProductAttribute } from "@core/schema/OrderProductInfo.js";
import type { OrderValidationResponse } from "@core/schema/OrderValidation.js";
import { PaymentType } from "@core/schema/PaymentType.js";
import type { PricingLevel } from "@core/schema/PricingLevel.js";
import type {
	ProductForCustomer,
	ProductOfVariantForCustomer,
	ProductVariantForCustomer,
} from "@core/schema/Product.js";
import type { SubcategoryForCustomer } from "@core/schema/Subcategory.js";
import type { Tenant } from "@core/schema/Tenant.js";
import { getOrderThankYouPath } from "@core/schema/paths/getOrderThankYouPath.js";
import { calculatePriceOfOrder } from "@core/schema/utils/calculatePriceOfOrder.js";
import { country } from "@core/schema/utils/country.js";
import type { OrderProductPriceSettings } from "@core/schema/utils/getPriceOfOrderProduct.js";
import { getProductOfVariant } from "@core/schema/utils/getProductOfVariant.js";
import { isSameOrderProduct } from "@core/schema/utils/isSameOrderProduct.js";
import { transformToOrderProduct } from "@core/schema/utils/transformToOrderProduct.js";
import type { DeepPartial } from "@core/utils/DeepPartial.js";
import { deepMerge } from "@core/utils/deepMerge.js";
import type { Payment } from "@core/utils/goPay/Payment.js";
import { isPplDeliveryType } from "@core/utils/isPplDeliveryType.js";
import { StorageState } from "@core/utils/svelte/StorageState.svelte.js";
import {
	onDeliveryTypeChanged,
	onPaymentTypeChanged,
	reportAddedProductToCart,
	reportPurchase,
	reportRemovedProductFromCart,
} from "../UniversalAnalytics.js";
import type { GlobalSettings } from "../contexts/GlobalSettings.js";
import { CartStep, type NextStep, cartStepArray, getCartStepPath, getNextStep } from "./CartStep.js";
import type { CartInStep, CartState, OrderAfterOverview } from "./CartType.js";
import { OrderUnpaidError } from "./OrderUnpaidError.js";
import { cartVersion } from "./cartVersion.js";
import { cartWithAppliedProperties } from "./cartWithAppliedProperties.js";
import type { FieldsToFillInPerStep } from "./fieldsToFillInPerStep.js";
import { getPriceDataFromOrder } from "./getPriceDataFromOrder.js";

const initialCart: CartState = cartWithAppliedProperties({
	productsInOrder: [],
	step: CartStep.PaymentDelivery,
	order: {
		products: [],
		deliveryAddress: {
			country: country("Czechia"),
		},
		subjectVatType: EconomicSubjectVatType.NotVatSubject,
	},
});

interface CartInitData {
	countrySettings: CountrySettings[];
	currency: Currency;
	destinationCountry: Country | "outsideEu";
	exchangeRates: ExchangeRate[];
	pricingLevel: PricingLevel;
	subcategories: SubcategoryForCustomer[];
	tenant: Tenant;
	vatType: EconomicSubjectVatType;
}

type FieldsToAdvanceFrom<Step extends CartStep> = Pick<CartInStep<Step>["order"], FieldsToFillInPerStep<Step>>;

class WebGlobalCart {
	private initData = $state<CartInitData>();

	private cart = $state<StorageState<CartState> | null>(null);
	public readonly currency = $derived(this.cart?.value.order.payment?.currency ?? this.initData?.currency);
	public readonly destinationCountry = $derived(
		this.cart?.value.order.deliveryAddress?.country ?? this.initData?.destinationCountry,
	);
	public readonly itemCount = $derived(
		this.cart?.value.order.products.reduce((sum, { quantity }) => sum + quantity, 0) ?? 0,
	);
	public readonly price = $derived(this.initData && this.computePrice(this.initData));
	public readonly totalPrice = $derived(this.price?.totalPrice);
	public readonly deliveryPrice = $derived(this.price?.deliveryPrice);
	public readonly loaded = $derived(this.initData !== undefined && this.cart?.loaded);
	public readonly appliedDiscounts = $derived(this.price?.appliedDiscounts);
	public vatType = $derived(this.cart?.value.order.subjectVatType);
	public showModal = $state(false);

	private reportAnalyticsOnValueChange<T>(
		newValue: T | undefined,
		reportCallback: (
			order: OrderAfterOverview,
			price: OrderPricesInput,
			locale: Locale,
			data: T,
			settings: OrderProductPriceSettings,
		) => void,
		changed: boolean,
	): void {
		untrack(() => {
			if (newValue === undefined || !this.initData || !this.cart || !changed) {
				return;
			}
			const { order, applyTax } = getPriceDataFromOrder(
				this.cart.value.order,
				this.initData.countrySettings,
				this.currency ?? this.initData.currency,
				this.destinationCountry ?? this.initData.destinationCountry,
				this.cart.value.order.subjectVatType ?? EconomicSubjectVatType.NotVatSubject,
			);
			const price = calculatePriceOfOrder(order, {
				...this.initData,
				customer: Auth.loggedCustomer,
				vatType: this.cart.value.order.subjectVatType ?? EconomicSubjectVatType.NotVatSubject,
			});

			reportCallback(order, price, this.initData.tenant.locale, newValue, { applyTax });
		});
	}

	public dispose(): void {
		// Set in init function.
	}

	public init(data: CartInitData): void {
		const cart = new StorageState(
			"local",
			`cart:${cartVersion}:${data.pricingLevel}:${Auth.loggedCustomer?.id ?? "unauth"}`,
			initialCart,
		);
		this.cart = cart;
		this.initData = data;
		this.cart.value.order = deepMerge(this.cart.value.order, { payment: { currency: data.currency } });
		this.updateProducts();

		this.dispose = $effect.root(() => {
			$effect(() => {
				void cart.value.order.deliveryAddress?.country;
				void cart.value.order.subjectVatType;
				this.updateProducts();
			});

			let deliveryChanged = false;
			$effect(() => {
				this.reportAnalyticsOnValueChange(cart.value.order.deliveryType, onDeliveryTypeChanged, deliveryChanged);
				deliveryChanged = true;
			});

			let paymentChanged = false;
			$effect(() => {
				this.reportAnalyticsOnValueChange(cart.value.order.payment?.type, onPaymentTypeChanged, paymentChanged);
				paymentChanged = true;
			});

			return (): void => {
				cart.dispose();
			};
		});
	}

	public updateProducts(): void {
		untrack(() => {
			if (!this.cart) {
				return;
			}
			for (const orderProduct of this.cart.value.order.products) {
				const product = this.findProduct(orderProduct);
				if (!product) {
					throw new Error(`Could not find product ${orderProduct.info.id}`);
				}
				this.updateProductInfoInBasket(orderProduct, product);
			}
		});
	}

	/**
	 * Adds a product to the cart. If the product already exists, it updates the quantity.
	 *
	 * @param insertionIndex Optional index at which to insert the product. If not provided, the product is inserted at the end.
	 * @returns `true` if the product was added, `false` if the product already existed and the quantity was updated
	 */
	public addProduct(
		product: ProductOfVariantForCustomer,
		attributes: OrderProductAttribute[],
		quantity: number,
		insertionIndex?: number,
	): boolean {
		if (!this.cart || !this.initData) {
			throw new Error("Cart not initialized.");
		}

		const productToAdd = transformToOrderProduct(product, attributes, this.initData, quantity);
		const existingProduct = this.cart.value.order.products.find((product) => isSameOrderProduct(product, productToAdd));

		if (existingProduct) {
			existingProduct.quantity += quantity;
			return false;
		}

		const productInCardAlreadyExists = this.cart.value.productsInOrder.some(
			({ id, variant }) => id === product.id && variant.id === product.variant.id,
		);
		if (!productInCardAlreadyExists) {
			this.cart.value.productsInOrder.push(product);
		}

		if (insertionIndex !== undefined) {
			this.cart.value.order.products.splice(insertionIndex, 0, productToAdd);
		} else {
			this.cart.value.order.products.push(productToAdd);
		}

		const { applyTax } = getPriceDataFromOrder(
			this.cart.value.order,
			this.initData.countrySettings,
			this.currency ?? this.initData.currency,
			this.destinationCountry ?? this.initData.destinationCountry,
			this.cart.value.order.subjectVatType ?? EconomicSubjectVatType.NotVatSubject,
		);
		reportAddedProductToCart(productToAdd, this.initData.tenant.locale, this.initData.currency, { applyTax });
		return true;
	}

	/**
	 * Removes a product from the cart. If quantity is provided, it removes the specified quantity, otherwise it removes the product entirely.
	 * Throws an error if the product index is out of bounds.
	 */
	public removeProduct(orderProduct: OrderProduct, quantity?: number): void {
		if (!this.cart || !this.initData) {
			throw new Error("Cart not initialized.");
		}
		const orderProductToRemoveIndex = this.cart.value.order.products.findIndex(({ id }) => id === orderProduct.id);
		const orderProductToRemove = this.cart.value.order.products[orderProductToRemoveIndex];
		if (!orderProductToRemove) {
			throw new Error("Index is out of bounds");
		}

		if (quantity === undefined || orderProductToRemove.quantity <= quantity) {
			this.cart.value.order.products.splice(orderProductToRemoveIndex, 1);
			this.cart.value.productsInOrder = this.cart.value.productsInOrder.filter(({ id, variant }) =>
				this.cart?.value.order.products.some(
					(orderProduct) => orderProduct.info.id === id && orderProduct.info.variant.id === variant.id,
				),
			);

			const { applyTax } = getPriceDataFromOrder(
				this.cart.value.order,
				this.initData.countrySettings,
				this.currency ?? this.initData.currency,
				this.destinationCountry ?? this.initData.destinationCountry,
				this.cart.value.order.subjectVatType ?? EconomicSubjectVatType.NotVatSubject,
			);
			reportRemovedProductFromCart(
				orderProductToRemove,
				this.initData.tenant.locale,
				this.currency ?? this.initData.currency,
				{ applyTax },
				orderProductToRemove.quantity,
			);

			return;
		}

		orderProductToRemove.quantity -= quantity;
	}

	public setProductQuantity(orderProduct: OrderProduct, newQuantity: number): void {
		if (!this.cart || !this.initData) {
			throw new Error("Cart not initialized.");
		}

		if (newQuantity === 0) {
			this.removeProduct(orderProduct);
			return;
		}

		const orderProductInCart = this.cart.value.order.products.find(({ id }) => id === orderProduct.id);
		if (!orderProductInCart) {
			throw new Error("Index is out of bounds");
		}
		orderProductInCart.quantity = newQuantity;
	}

	public updateProductInfoInBasket(orderProduct: OrderProduct, updated: ProductForCustomer): void {
		if (!this.cart || !this.initData) {
			throw new Error("Cart not initialized.");
		}
		const orderProductIndex = this.cart.value.order.products.findIndex(({ id }) => id === orderProduct.id);
		if (orderProductIndex === -1) {
			return;
		}

		const productVariant = getProductOfVariant(updated, orderProduct.info.variant.id);

		this.cart.value.order.products[orderProductIndex] = {
			...transformToOrderProduct(
				productVariant,
				orderProduct.info.attributes,
				{
					currency: this.currency ?? this.initData.currency,
					destinationCountry: this.destinationCountry ?? this.initData.destinationCountry,
					subcategories: this.initData.subcategories,
					vatType: this.cart.value.order.subjectVatType ?? EconomicSubjectVatType.NotVatSubject,
				},
				orderProduct.quantity,
			),
			id: orderProduct.id,
		};
	}

	public updateProductVariantInBasket(orderProduct: OrderProduct, variantId: ProductVariantForCustomer["id"]): void {
		if (!this.cart || !this.initData) {
			throw new Error("Cart not initialized.");
		}

		const product = this.findProduct(orderProduct);
		if (!product) {
			throw new Error("The requested product could not be found.");
		}

		const newProduct = getProductOfVariant(product, variantId);

		this.removeProduct(orderProduct);
		this.addProduct(
			newProduct,
			orderProduct.info.attributes,
			orderProduct.quantity,
			this.cart.value.order.products.findIndex(({ id }) => id === orderProduct.id),
		);
	}

	public clear(): void {
		if (!this.cart || !this.initData) {
			throw new Error("Cart not initialized.");
		}
		this.cart.value = initialCart;
	}

	public readonly state = $derived(this.cart?.value);
	public readonly orderProducts = $derived(this.cart?.value.order.products ?? []);
	public readonly productsInOrder = $derived(this.cart?.value.productsInOrder ?? []);

	public findProduct(orderProduct: OrderProduct): ProductOfVariantForCustomer | undefined {
		return this.productsInOrder.find(
			({ id, variant }) => id === orderProduct.info.id && variant.id === orderProduct.info.variant.id,
		);
	}

	private computePrice(initData: CartInitData): OrderPricesInput {
		if (!this.cart || !this.initData) {
			return {
				appliedDiscounts: {
					deliveryPrice: 0,
					discountsByTaxRate: [],
				},
				deliveryPrice: {
					taxRate: 1,
					withTax: 0,
					withoutTax: 0,
				},
				roundingAdjustment: 0,
				totalPrice: {
					withTax: 0,
					withoutTax: 0,
				},
			};
		}

		const { order } = getPriceDataFromOrder(
			this.cart.value.order,
			initData.countrySettings,
			this.currency ?? initData.currency,
			this.destinationCountry ?? initData.destinationCountry,
			this.cart.value.order.subjectVatType ?? EconomicSubjectVatType.NotVatSubject,
		);
		return calculatePriceOfOrder(order, {
			...initData,
			customer: Auth.loggedCustomer,
			vatType: this.cart.value.order.subjectVatType ?? EconomicSubjectVatType.NotVatSubject,
		});
	}

	public saveFields<CurrentCart extends CartState = CartState>(fields: DeepPartial<CurrentCart["order"]>): CurrentCart {
		if (!this.cart || !this.initData) {
			throw new Error("Cart not initialized.");
		}

		this.cart.value.order = deepMerge(this.cart.value.order, fields);
		return this.cart.value as CurrentCart;
	}

	public advanceToNextStep<Step extends keyof { [K in CartStep as NextStep<K> extends never ? never : K]: never }>(
		currentStep: Step,
		fields: FieldsToAdvanceFrom<Step>,
		noRedirect = false,
	): CartInStep<NextStep<Step>> {
		if (this.initData === undefined) {
			throw new Error("Cart not initialized");
		}

		const cart = this.getCorrectCartInStepOrThrow<CartStep>(currentStep) as CartInStep<NextStep<Step>>;
		this.saveFields(fields);

		if (cart.productsInOrder.length === 0) {
			throw new Error("Cannot advance to next step - no products in order");
		}

		const cartStepIndex = cartStepArray.indexOf(currentStep);
		const nextStep = getNextStep(currentStep);
		const nextStepIndex = cartStepArray.indexOf(nextStep);

		if (cartStepIndex > nextStepIndex) {
			throw new Error("Cannot advance to previous step");
		}

		if (!noRedirect && cartStepIndex < nextStepIndex) {
			location.replace(getCartStepPath(nextStep, this.initData.tenant));
		}

		cart.step = nextStep as typeof cart.step;
		return cart;
	}

	public getCorrectCartInStepOrRedirect<Step extends CartStep>(
		tenant: Tenant,
		inStep: Step,
	): {
		cartInStep: CartInStep<Step>;
		redirected: boolean;
	} {
		let redirected = false;

		if (typeof window === "undefined" || !this.cart || !this.initData) {
			return {
				cartInStep: (this.cart?.value ?? initialCart) as CartInStep<Step>,
				redirected,
			};
		}

		const cartStepIndex = cartStepArray.indexOf(this.cart.value.step);
		const stepIndex = cartStepArray.indexOf(inStep);

		if (cartStepIndex < stepIndex) {
			location.replace(getCartStepPath(this.cart.value.step, tenant));
			redirected = true;
		}

		return {
			cartInStep: this.cart.value as CartInStep<Step>,
			redirected,
		};
	}

	public getCorrectCartInStepOrThrow<Step extends CartStep>(inStep: Step): CartInStep<Step> {
		if (typeof window === "undefined" || !this.cart) {
			return (this.cart?.value ?? initialCart) as CartInStep<Step>;
		}

		const cartStepIndex = cartStepArray.indexOf(this.cart.value.step);
		const stepIndex = cartStepArray.indexOf(inStep);

		if (cartStepIndex < stepIndex) {
			throw new Error(`Cannot get cart in step ${inStep} - the cart is at step ${this.cart.value.step}`);
		}

		return this.cart.value as CartInStep<Step>;
	}

	private markAsSent(order: PublicOrder): void {
		if (!this.cart) {
			throw new Error("Cart not initialized");
		}
		if (this.cart.value.step !== CartStep.ReadyToSend) {
			throw new Error("Cannot mark order as sent - cart is not in ReadyToSend step");
		}
		this.cart.value.sentOrder = order;
	}

	public async completeOrder(globalSettings: GlobalSettings): Promise<void> {
		if (!this.cart || !this.initData) {
			throw new Error("Cart not initialized.");
		}
		const { order } = this.getCorrectCartInStepOrThrow(CartStep.ReadyToSend);
		const { graphQL } = await getClients(globalSettings);

		const prices = calculatePriceOfOrder(order, {
			...this.initData,
			customer: Auth.loggedCustomer,
			vatType: order.subjectVatType,
		});

		if (!isPplDeliveryType(order.deliveryType) && order.deliveryAddress.pplDeliveryPoint) {
			delete order.deliveryAddress.pplDeliveryPoint;
		}

		const result = await graphQL.mutate<{ input: OrderInput }, Order>({
			mutation: createOrderMutation,
			variables: {
				input: {
					...order,
					prices,
					locale: this.initData.tenant.locale,
				},
			},
		});
		this.markAsSent(result);

		reportPurchase(result);

		if (result.goPayData?.url) {
			const gatewayUrl = result.goPayData.url;
			const returnUrl = await new Promise<URL>((resolve) => {
				_gopay.checkout({ gatewayUrl, inline: true }, (response) => {
					resolve(new URL(response.url));
				});
			});
			const id = Number(new URLSearchParams(returnUrl.search).get("id"));
			const status = await graphQL.query<GoPayPaymentState, Pick<Payment, "id">>({
				query: getPaymentStatusQuery,
				variables: { id },
			});
			if (status !== GoPayPaymentState.Paid) {
				throw new OrderUnpaidError();
			}
			location.assign(returnUrl);
		} else {
			location.assign(getOrderThankYouPath(this.initData.tenant.locale, result.b2cDetailId));
		}
	}

	public patchInvalidOrder(response: OrderValidationResponse): void {
		if (!this.initData) {
			throw new Error("Cart not initialized");
		}

		const cart = this.getCorrectCartInStepOrThrow(CartStep.ReadyToSend);

		for (const unavailableProduct of response.unavailable) {
			this.removeProduct(unavailableProduct);
		}

		for (const [productToUpdate, updatedProduct] of response.toUpdate) {
			this.updateProductInfoInBasket(productToUpdate, updatedProduct);
		}

		if (response.invalidPaymentType) {
			cart.order.payment.type = PaymentType.GoPayPaymentCard;
		}

		if (response.invalidDeliveryType) {
			cart.order.deliveryType = DeliveryType.DeliveryToAddress;
		}

		if (response.correctPrice !== true) {
			// TODO: How to patch?
		}
	}
}

export const Cart = new WebGlobalCart();
